import { z } from 'zod';
/**
 * The provider an organisation sends through, and its own credentials.
 *
 * Credentials travel in one direction only. They are written and never read
 * back: the response says which provider is configured, which fields have a
 * value and when it was saved, and never what any value is. A route that
 * returned them would be one refactor away from putting somebody's API key in a
 * browser's network log.
 *
 * `sendsAs` is the one exception and is not a credential: it is the address
 * recipients read in the From line of a Gmail connection. See `sendsAs` below.
 *
 * That means the form arrives empty even when a provider is configured, and
 * saving replaces the whole record. Both are consequences worth paying: a form
 * pre-filled with dots that submit as dots is how half a credential gets saved.
 */
export const emailProviderSchema = z.enum(['ses', 'postmark', 'resend', 'brevo', 'smtp', 'gmail']);
export const providerConfigurationSchema = z.object({
    /** Null when this organisation sends through the deployment's own provider. */
    provider: emailProviderSchema.nullable(),
    /** The keys that have a value. Enough to say "configured" without saying what. */
    filledKeys: z.array(z.string()),
    updatedAt: z.iso.datetime().nullable(),
    /**
     * The address the provider is locked to sending as, or null.
     *
     * Only Gmail has one, and it is what makes "connected" mean something: a
     * screen that could say Gmail was configured but not *which account* is a
     * screen that looks identical whether the right person authorised it or the
     * wrong one did. It is also what the campaign review compares the From
     * address against, because Gmail refuses anything else.
     */
    sendsAs: z.string().nullable(),
});
export const saveProviderRequestSchema = z.object({
    provider: emailProviderSchema,
    /**
     * Every field the provider takes, by key.
     *
     * A record rather than a shape per provider, because the fields are data —
     * `PROVIDER_FIELDS` in the domain — and a schema per provider here would be a
     * second list to keep in step. Which of them are required is checked against
     * that list, where the answer already lives.
     */
    settings: z.record(z.string(), z.string().max(4096)),
});
/**
 * What a test send did.
 *
 * Every outcome is a 200 with a status: a provider refusing a message is an
 * answer about the credentials, which is exactly what was asked, rather than a
 * failed request.
 */
export const testSendResponseSchema = z.object({
    status: z.enum(['sent', 'refused', 'unknown', 'not-configured']),
    /** The provider's own sentence, when it gave one. */
    detail: z.string().nullable(),
});
/**
 * Where to send somebody to authorise Gmail.
 *
 * The route answers with the url rather than a 302, because the browser has to
 * present a credential to reach it and the credential is an `Authorization`
 * header this application sets — a plain link would arrive anonymous. So the
 * page fetches, then navigates, and the trip to Google is one hop later than
 * it used to be and otherwise identical.
 */
export const connectGoogleStartSchema = z.object({ url: z.url() });
//# sourceMappingURL=emailProvider.js.map